// create-release.ts — the Create-release modal's DECISION SURFACES (seam
// create-release-modal; R50/R53/R54/R55). Pure functions only: home.ts owns
// the DOM, this module owns every judgment the form makes, so
// home.test.mjs can drill the exact logic the mounted dialog runs (the
// relative-time.ts second-bundle precedent — no jsdom in the dep fence).
//
// THE VALIDATION CONTRACT (ruling Q6): 'valid' = syntactic legality (the
// server's safeBranch rules, ported below) + membership in the CACHED
// branch list (/api/vcs/branch-names, fetched ONCE per dialog open — never
// a per-keystroke network call; the create path already blocks on an
// ls-remote and start-latency.test.mjs exists because this repo fought
// that latency once). The remote gate at submit stays AUTHORITATIVE: a
// cache miss shows the invalid state but never blocks the POST (the mirror
// can trail a just-pushed branch); only a syntactically illegal name is
// refused client-side, because the server would refuse it too.
//
// THE PREFILL CONTRACT (ruling Q7, R55): the console seat
// ConsoleConfig.defaultSourceBranch wins; the corpus code seat's
// default_branch is DISPLAYED when the console seat is unset; when both are
// set and disagree the tooltip names BOTH (the modal would otherwise show
// one branch while a blank submit cut another). The example default
// ('elite/1_Dev') RIDES A SEAT — no project branch name is ever a literal
// in this file.
//
// COPY CONSTRAINT: the branch field is the CUT BASE only. codeBranchName
// (server corpus/branches.ts) puts the release on release/<slug>; no copy
// here may imply the release lives on the browsed branch. The hint string
// is exported so the test pins the constraint.

/** The cut-base copy, pinned by home.test.mjs: names what the branch IS
 * (the base the release is cut from) and where the release actually lives
 * (its own release/<slug> branch) — never "the release's branch". */
export const CREATE_BRANCH_HINT =
  'Cut base: the release is cut FROM this branch and lands on its own release/<slug> branch.';

/** Placeholder when neither seat names a default. */
export const CREATE_NO_DEFAULT_PLACEHOLDER = '(no default branch configured)';

/** Syntactic legality — the server's safeBranch() rules (safe-branch.ts)
 * ported verbatim: refuse empty, leading '-', whitespace, and any of
 * ~ ^ : ? * [ \  (refs are not arbitrary revisions; a flag-shaped or
 * glob-shaped name never reaches a git argv server-side either). */
export function clientSafeBranch(b: string): boolean {
  const v = b.trim();
  if (!v || v.startsWith('-') || /[\s~^:?*[\\]/.test(v)) return false;
  return true;
}

/** The reason line for a syntactically illegal name — same wording family
 * as the server's branch-validate refusal, so both lanes read as one rule. */
export const BRANCH_SYNTAX_REASON =
  'Not a legal branch name: empty, leading -, whitespace, or one of ~ ^ : ? * [ \\';

/** Candidate ranking, ported from the server's BrowseService.validate
 * (corpus/browse.ts — itself the create gate's branches.ts precedent):
 * caseless exact, then caseless last-segment, then caseless substring
 * either way; ties alphabetical; top 5. */
export function branchCandidates(name: string, names: string[]): string[] {
  const want = name.trim().toLowerCase();
  if (!want) return [];
  const wantTail = want.split('/').pop() ?? want;
  const rank = (h: string): number => {
    const low = h.toLowerCase();
    const tail = low.split('/').pop() ?? low;
    if (low === want) return 0;
    if (tail === want || tail === wantTail) return 1;
    if (low.includes(want) || want.includes(low)) return 2;
    return 3;
  };
  return names
    .map((h) => ({ h, r: rank(h) }))
    .filter((x) => x.r < 3)
    .sort((a, b) => a.r - b.r || a.h.localeCompare(b.h))
    .map((x) => x.h)
    .slice(0, 5);
}

export type BranchFieldState =
  /** Blank field: the server applies its own default (or no gate at all). */
  | 'empty'
  /** Fails safeBranch's shape rules — the ONE state that blocks submit. */
  | 'invalid-syntax'
  /** No cached list (corpus seat unset / fetch failed): syntactic-only. */
  | 'unchecked'
  /** Present in the cached branch list. */
  | 'listed'
  /** Legal shape, not in the cached list — remote gate decides at submit. */
  | 'unlisted';

export interface BranchFieldVm {
  state: BranchFieldState;
  /** R54: the field renders the first-class INVALID presentation. */
  invalid: boolean;
  /** Q6: only 'invalid-syntax' blocks the POST; the remote stays authoritative. */
  submitOk: boolean;
  /** Field-level line (null = quiet). Candidates render as their own chips. */
  message: string | null;
  candidates: string[];
}

/** The per-edit judgment — pure and synchronous (R53 'instant'). `names` is
 * the cached population from /api/vcs/branch-names, or null when that lane
 * is unavailable (dev installs without a corpus seat degrade to
 * syntactic-only, named in the state — never a silent pass-as-valid). */
export function branchFieldVm(raw: string, names: string[] | null): BranchFieldVm {
  const v = raw.trim();
  if (!v) {
    return { state: 'empty', invalid: false, submitOk: true, message: null, candidates: [] };
  }
  if (!clientSafeBranch(v)) {
    return {
      state: 'invalid-syntax', invalid: true, submitOk: false,
      message: BRANCH_SYNTAX_REASON, candidates: [],
    };
  }
  if (names === null) {
    return { state: 'unchecked', invalid: false, submitOk: true, message: null, candidates: [] };
  }
  if (names.includes(v)) {
    return {
      state: 'listed', invalid: false, submitOk: true,
      message: 'Resolves in the cached branch list.', candidates: [],
    };
  }
  return {
    state: 'unlisted', invalid: true, submitOk: true,
    message:
      `Not in the cached branch list (${names.length} branches) — ` +
      'the remote gate at submit decides.',
    candidates: branchCandidates(v, names),
  };
}

// ---------------------------------------------------------------------
// Owner batch 2026-09-05 (docs/create-release-browsers.md): per-field
// validity ICONS (D3), the disabled-until-green gate (D1), the name
// availability lane (D2), the sha 'Latest' contract, and the source-branch
// typeahead (D6). All pure — create-dialog.ts owns the DOM.
// ---------------------------------------------------------------------

/** The server's slugify (releases.ts), ported verbatim — the availability
 * probe answers for the TRIMMED name; the gate below mirrors the create
 * path's shape refusals so the field can refuse before any round trip. */
export function clientSlugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

/** The create-time name gate (server nameGate, sentences identical). */
export function clientNameGate(
  raw: string,
): { ok: true; slugBase: string } | { ok: false; error: string } {
  const name = raw.trim();
  if (!name) return { ok: false, error: 'name required' };
  const base = clientSlugify(name);
  if (!base) return { ok: false, error: `name refused: '${name}' yields an empty slug` };
  if (base.endsWith('-test')) {
    return {
      ok: false,
      error:
        `name refused: slug '${base}' ends in '-test' — reserved for test-server ` +
        `subdomains ({slug}-test.{apex})`,
    };
  }
  if (base.startsWith('replay-')) {
    return {
      ok: false,
      error:
        `name refused: slug '${base}' starts with 'replay-' — reserved for REPLAY-class ` +
        `rows (POST /api/replays creates those)`,
    };
  }
  return { ok: true, slugBase: base };
}

/** What the in-field icon seat shows (D3): a check, an X, or nothing. The
 * tip is the WHOLE verdict sentence — it rides data-tip/aria-label and the
 * field's visually-hidden live line, never a visible text row. */
export interface ValidityIconVm {
  kind: 'ok' | 'bad' | 'none';
  /** Green = counts toward the submit gate (item 7). */
  green: boolean;
  tip: string | null;
}

/** The debounced probe lanes, as data. 'idle' = nothing asked (empty field,
 * local refusal, or the debounce window still open); 'pending' = in flight. */
export type NameProbe =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'free'; measured: string }
  | { state: 'taken'; reason: string; measured: string }
  | { state: 'error'; detail: string };

export const NAME_CHECKING_TIP = 'checking name availability…';
export const NAME_PROBE_DEGRADE =
  'availability check unavailable — the create gate at submit stays authoritative';

/** Item 2 — the Name field's verdict. A pending probe is NEVER a
 * provisional green; a probe ERROR degrades green by name (blocking every
 * create because the probe lane failed would be the bigger lie — the DB
 * UNIQUE still refuses at submit). */
export function nameFieldVm(raw: string, probe: NameProbe): ValidityIconVm {
  const v = raw.trim();
  if (!v) return { kind: 'none', green: false, tip: null };
  const gate = clientNameGate(v);
  if (!gate.ok) return { kind: 'bad', green: false, tip: gate.error };
  switch (probe.state) {
    case 'free':
      return { kind: 'ok', green: true, tip: `name available — ${probe.measured}` };
    case 'taken':
      return { kind: 'bad', green: false, tip: `${probe.reason} — ${probe.measured}` };
    case 'error':
      return { kind: 'ok', green: true, tip: `${NAME_PROBE_DEGRADE} (${probe.detail})` };
    default:
      return { kind: 'none', green: false, tip: NAME_CHECKING_TIP };
  }
}

export type ShaProbe =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'resolved'; measured: string }
  | { state: 'unresolved'; detail: string }
  | { state: 'error'; detail: string };

export const SHA_LATEST_PLACEHOLDER = 'Latest';
export const SHA_EMPTY_TIP =
  "empty = 'Latest': the cut uses the tip of the cut base branch";
export const SHA_SYNTAX_REASON = 'not a sha: want 4-40 hex characters';
export const SHA_PROBE_DEGRADE =
  'sha resolve unavailable — the cut gate at submit stays authoritative';

/** Item 6 — the Source SHA verdict. EMPTY IS GREEN by contract (the
 * 'Latest' placeholder is valid input); a typed sha must be hex 4-40
 * instantly and resolve on the mirror when the debounced probe answers. */
export function shaFieldVm(raw: string, probe: ShaProbe): ValidityIconVm {
  const v = raw.trim();
  if (!v) return { kind: 'ok', green: true, tip: SHA_EMPTY_TIP };
  if (!/^[0-9a-f]{4,40}$/i.test(v)) return { kind: 'bad', green: false, tip: SHA_SYNTAX_REASON };
  switch (probe.state) {
    case 'resolved':
      return { kind: 'ok', green: true, tip: probe.measured };
    case 'unresolved':
      return { kind: 'bad', green: false, tip: probe.detail };
    case 'error':
      return { kind: 'ok', green: true, tip: `${SHA_PROBE_DEGRADE} (${probe.detail})` };
    default:
      return { kind: 'none', green: false, tip: 'resolving sha on the code mirror…' };
  }
}

export const BRANCH_CACHE_DEGRADE =
  'branch cache unavailable — syntactic check only; the cut gate at submit stays authoritative';

/** Item 1/7 — the branch verdict as an icon. 'listed' is the real green;
 * 'unchecked' (no cache lane) degrades to syntactic green BY NAME; an
 * 'unlisted' name is an X — this deliberately supersedes Q6's
 * cache-misses-still-submit ruling (docs/create-release-browsers.md D1). */
export function branchIconVm(vm: BranchFieldVm): ValidityIconVm {
  switch (vm.state) {
    case 'empty':
      return { kind: 'none', green: false, tip: null };
    case 'invalid-syntax':
      return { kind: 'bad', green: false, tip: vm.message ?? BRANCH_SYNTAX_REASON };
    case 'unchecked':
      return { kind: 'ok', green: true, tip: BRANCH_CACHE_DEGRADE };
    case 'listed':
      return { kind: 'ok', green: true, tip: vm.message };
    case 'unlisted':
      return { kind: 'bad', green: false, tip: vm.message };
  }
}

/** Item 7 — the confirm button enables only when every field is green. */
export function createSubmitOk(
  name: ValidityIconVm, branch: ValidityIconVm, sha: ValidityIconVm,
): boolean {
  return name.green && branch.green && sha.green;
}

// ---------------------------------------------------------------------
// Item 8 — the source-branch typeahead (segment walk over the cached,
// deliberately-uncapped /api/vcs/branch-names population).
// ---------------------------------------------------------------------

export interface TypeaheadOption {
  /** What accepting this option puts in the input (dirs end in '/'). */
  value: string;
  /** The display segment ('users/' or the leaf name). */
  label: string;
  kind: 'dir' | 'branch';
  /** Branches under a dir option (dirs only; branches carry 1). */
  leafCount: number;
}

export interface TypeaheadVm {
  options: TypeaheadOption[];
  /** Options before the cap — the '+N more' tail is measured, not vibes. */
  total: number;
  overflow: number;
}

export const TYPEAHEAD_CAP = 40;

/** Next-segment suggestions for the typed prefix: dirs first then leaves,
 * alphabetical, caseless prefix match on the PARTIAL segment (display keeps
 * the branch's real casing). Pure and synchronous — every keystroke judges
 * against the cache, never the network (the modal-open fetch IS the cache
 * load, measured by typeaheadBootLine). */
export function typeaheadOptions(
  input: string, names: string[], cap = TYPEAHEAD_CAP,
): TypeaheadVm {
  const v = input.trimStart();
  const slash = v.lastIndexOf('/');
  const prefix = slash === -1 ? '' : v.slice(0, slash + 1);
  const prefixLow = prefix.toLowerCase();
  const partial = (slash === -1 ? v : v.slice(slash + 1)).toLowerCase();
  const dirs = new Map<string, { label: string; count: number }>();
  const leaves: string[] = [];
  for (const n of names) {
    const low = n.toLowerCase();
    if (!low.startsWith(prefixLow)) continue;
    const restLow = low.slice(prefix.length);
    if (!restLow.startsWith(partial)) continue;
    const rest = n.slice(prefix.length);
    const cut = rest.indexOf('/');
    if (cut !== -1) {
      const seg = rest.slice(0, cut);
      const k = seg.toLowerCase();
      const got = dirs.get(k);
      if (got) got.count++;
      else dirs.set(k, { label: seg, count: 1 });
    } else {
      leaves.push(n);
    }
  }
  const opts: TypeaheadOption[] = [
    ...[...dirs.values()]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((d) => ({
        value: `${prefix}${d.label}/`, label: `${d.label}/`,
        kind: 'dir' as const, leafCount: d.count,
      })),
    ...leaves
      .sort((a, b) => a.localeCompare(b))
      .map((n) => ({
        value: n, label: n.slice(prefix.length), kind: 'branch' as const, leafCount: 1,
      })),
  ];
  // The exact already-typed leaf as the ONLY option suggests nothing new.
  if (opts.length === 1 && opts[0]!.kind === 'branch' && opts[0]!.value === v.trim()) {
    return { options: [], total: 0, overflow: 0 };
  }
  return {
    options: opts.slice(0, cap),
    total: opts.length,
    overflow: Math.max(0, opts.length - cap),
  };
}

/** The measured cache-load line (D6) — logged once per dialog open. */
export function typeaheadBootLine(count: number, ms: number, serverMeasured?: string): string {
  const base = `[create] branch typeahead cache: ${count} branches in ${Math.round(ms)} ms`;
  return serverMeasured ? `${base} — ${serverMeasured}` : base;
}

export interface CreateSeatConfig {
  default_source_branch?: string | null;
  corpus_default_branch?: string | null;
}

export interface PrefillVm {
  /** What the field starts as (may be '' when neither seat is set). */
  value: string;
  /** The field tooltip — names the SEAT(S) the value rides (Q7). */
  title: string;
  /** Placeholder when value is '' (else ''). */
  placeholder: string;
}

/** The Q7 seat arbitration, as data. Console seat wins; corpus seat is
 * displayed when the console seat is unset; a disagreement surfaces BOTH
 * by name in the tooltip. */
export function prefillVm(cc: CreateSeatConfig | null): PrefillVm {
  const consoleSeat = cc?.default_source_branch?.trim() || null;
  const corpusSeat = cc?.corpus_default_branch?.trim() || null;
  if (consoleSeat && corpusSeat && consoleSeat !== corpusSeat) {
    return {
      value: consoleSeat,
      title:
        `Prefilled from the console seat defaultSourceBranch ('${consoleSeat}'). ` +
        `The corpus code seat default_branch is '${corpusSeat}' — the cut uses the branch in this field.`,
      placeholder: '',
    };
  }
  if (consoleSeat) {
    return {
      value: consoleSeat,
      title:
        `Prefilled from the console seat defaultSourceBranch ('${consoleSeat}').` +
        (corpusSeat ? ' The corpus code seat default_branch agrees.' : ''),
      placeholder: '',
    };
  }
  if (corpusSeat) {
    return {
      value: corpusSeat,
      title:
        `Console seat defaultSourceBranch unset — showing the corpus code seat default_branch ('${corpusSeat}').`,
      placeholder: '',
    };
  }
  return {
    value: '',
    title:
      'No default source branch configured (console seat defaultSourceBranch and ' +
      'corpus code seat default_branch both unset).',
    placeholder: CREATE_NO_DEFAULT_PLACEHOLDER,
  };
}
